import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useSchoolSearch } from 'src/hooks/useSchoolSearch';
import RadioToggleOff from 'src/assets/images/radio-toggle-off.svg';
import SearchIcon from 'src/assets/images/search-icon.svg';
import styles from 'src/components/flows/styles/FindYourSchool.module.scss';
// Constants
const CONTENT = {
    title: 'Find your school',
    searchPlaceholder: 'Enter ZIP code here',
    searchAriaLabel: 'Enter ZIP code to search for schools',
    selectSchoolAriaLabel: 'Select',
    schoolNotListed: 'School not listed',
};
const SCHOOL_NOT_LISTED_ID = 'school-not-listed';
/**
 * FindYourSchool step component
 * Allows users to search for and select their school by ZIP code
 * @param {StepProps<FindYourSchoolData>} props - Component props
 * @param {FindYourSchoolData} props.initialData - Initial form data to restore state
 * @param {Function} props.onDataChange - Callback when form data changes
 * @returns {JSX.Element} FindYourSchool component
 */
const FindYourSchool = ({ initialData, onDataChange, setTitle, onPageValid, }) => {
    const [zipCode, setZipCode] = useState(initialData?.zipCode || '');
    const [selectedSchool, setSelectedSchool] = useState(initialData?.schoolNotListed
        ? SCHOOL_NOT_LISTED_ID
        : initialData?.selectedSchool?.id || null);
    const [selectedSchoolObj, setSelectedSchoolObj] = useState(initialData?.selectedSchool || null);
    const [schoolNotListed, setSchoolNotListed] = useState(initialData?.schoolNotListed || false);
    // Use the school search hook for API integration
    const { schools, loading, searchSchools } = useSchoolSearch(300);
    useEffect(() => {
        setTitle?.(CONTENT.title);
        onPageValid?.(!!initialData?.selectedSchool || !!initialData?.schoolNotListed);
        // If we have initial zip code, trigger search to populate schools
        if (initialData?.zipCode && initialData.zipCode.length >= 3) {
            searchSchools(initialData.zipCode);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    // Trigger search when ZIP code changes
    useEffect(() => {
        if (zipCode.length >= 3) {
            searchSchools(zipCode);
        }
    }, [zipCode, searchSchools]);
    // Sync data to parent whenever zipCode, selectedSchoolObj, or schoolNotListed changes
    useEffect(() => {
        onDataChange?.({
            zipCode,
            selectedSchool: selectedSchoolObj,
            schoolNotListed,
        });
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [zipCode, selectedSchoolObj, schoolNotListed]);
    /**
     * Handles school selection from the list
     * @param {string} schoolId - ID of the selected school or SCHOOL_NOT_LISTED_ID
     * @returns {void}
     */
    const handleSchoolSelect = (schoolId) => {
        if (schoolId === SCHOOL_NOT_LISTED_ID) {
            setSelectedSchool(SCHOOL_NOT_LISTED_ID);
            setSelectedSchoolObj(null);
            setSchoolNotListed(true);
            onPageValid?.(true);
        }
        else {
            const school = schools.find((s) => s.id === schoolId);
            setSelectedSchool(schoolId);
            setSelectedSchoolObj(school || null);
            setSchoolNotListed(false);
            onPageValid?.(true);
        }
    };
    /**
     * Handles ZIP code input changes
     * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
     * @returns {void}
     */
    const handleZipCodeChange = (e) => {
        setZipCode(e.target.value);
    };
    return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.searchSection, children: [_jsx("div", { className: styles.searchWrapper, children: _jsxs("div", { className: styles.inputWrapper, children: [_jsx("img", { src: SearchIcon, alt: "", className: styles.searchIcon, "aria-hidden": "true" }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: CONTENT.searchPlaceholder, value: zipCode, onChange: handleZipCodeChange, "aria-label": CONTENT.searchAriaLabel, maxLength: 5, pattern: "\\d*" })] }) }), _jsx("div", { className: styles.schoolsList, children: loading ? (_jsx("div", { className: styles.spinnerContainer, children: _jsx("div", { className: styles.spinner, "aria-label": "Loading schools" }) })) : (_jsxs(_Fragment, { children: [schools.length > 0 &&
                                schools.map((school) => (_jsxs("button", { type: "button", className: `${styles.schoolRow} ${selectedSchool === school.id ? styles.selected : ''}`, onClick: () => handleSchoolSelect(school.id), "aria-label": `${CONTENT.selectSchoolAriaLabel} ${school.name}`, role: "radio", "aria-checked": selectedSchool === school.id, children: [_jsxs("div", { className: styles.schoolInfo, children: [_jsxs("h3", { className: styles.schoolName, "data-tooltip": `${school.name}${school.grades?.display ? ` (${school.grades.display})` : ''}`, children: [school.name, ' ', school.grades?.display && `(${school.grades.display})`] }), _jsxs("div", { className: styles.detailsRow, children: [school.address && (_jsxs("div", { className: styles.addressRow, "data-tooltip": `${school.address.street}, ${school.address.city}, ${school.address.state} ${school.address.zipCode}`, children: [_jsx("span", { children: school.address.street }), _jsx("span", { children: school.address.city }), _jsx("span", { children: school.address.state }), _jsx("span", { children: school.address.zipCode })] })), school.address && school.district?.name && (_jsx("div", { className: styles.divider })), school.district?.name && (_jsx("div", { className: styles.districtWrapper, "data-tooltip": school.district.name, children: _jsx("span", { className: styles.district, children: school.district.name }) }))] })] }), _jsx("div", { className: `${styles.radioButton} ${selectedSchool === school.id ? styles.checked : ''}`, "aria-hidden": "true", children: selectedSchool !== school.id && (_jsx("img", { src: RadioToggleOff, alt: "" })) })] }, school.id))), zipCode.length >= 3 && (_jsxs("button", { type: "button", className: `${styles.schoolRow} ${selectedSchool === SCHOOL_NOT_LISTED_ID ? styles.selected : ''}`, onClick: () => handleSchoolSelect(SCHOOL_NOT_LISTED_ID), "aria-label": `${CONTENT.selectSchoolAriaLabel} ${CONTENT.schoolNotListed}`, role: "radio", "aria-checked": selectedSchool === SCHOOL_NOT_LISTED_ID, children: [_jsx("div", { className: styles.schoolInfo, children: _jsx("h3", { className: styles.schoolName, children: CONTENT.schoolNotListed }) }), _jsx("div", { className: `${styles.radioButton} ${selectedSchool === SCHOOL_NOT_LISTED_ID ? styles.checked : ''}`, "aria-hidden": "true", children: selectedSchool !== SCHOOL_NOT_LISTED_ID && (_jsx("img", { src: RadioToggleOff, alt: "" })) })] }, SCHOOL_NOT_LISTED_ID))] })) })] }) }));
};
export default FindYourSchool;
